import type { ReactNode } from 'react';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { DATE_TIME_FORMAT } from '@/utils/dateFormat';

/** Visual configuration for each payment status used in tags and avatars. */
export interface StatusConfig {
  color: string;
  icon: ReactNode;
  label: string;
  /** Theme token holding the light status tint used behind the avatar icon. */
  bgToken: 'colorPrimaryBg' | 'colorSuccessBg' | 'colorErrorBg' | 'colorWarningBg' | 'colorFillTertiary';
}

/** Returns the tag colour, icon, label, and avatar background for a given status string. */
export const getStatusConfig = (status: string, t?: (key: string) => string): StatusConfig => {
  const tFn = t || ((key: string) => key);
  const configs: Record<string, StatusConfig> = {
    pending: {
      color: 'processing',
      icon: <ClockCircleOutlined />,
      label: tFn('status.pending'),
      bgToken: 'colorPrimaryBg',
    },
    completed: {
      color: 'success',
      icon: <CheckCircleOutlined />,
      label: tFn('status.completed'),
      bgToken: 'colorSuccessBg',
    },
    failed: {
      color: 'error',
      icon: <CloseCircleOutlined />,
      label: tFn('status.failed'),
      bgToken: 'colorErrorBg',
    },
    cancelled: {
      color: 'default',
      icon: <CloseCircleOutlined />,
      label: tFn('status.cancelled'),
      bgToken: 'colorFillTertiary',
    },
    refunded: {
      color: 'orange',
      icon: <RollbackOutlined />,
      label: tFn('status.refunded'),
      bgToken: 'colorWarningBg',
    },
  };

  return configs[status.toLowerCase()] ?? {
    color: 'default',
    icon: <ClockCircleOutlined />,
    label: status,
    bgToken: 'colorFillTertiary',
  };
};

/** Formats a UNIX timestamp into a human-readable date string. */
export const formatPaymentTimestamp = (timestamp: number) =>
  dayjs.unix(timestamp).format(DATE_TIME_FORMAT);

/** Returns `true` if the given UNIX expiry timestamp is in the past. */
export const isPaymentExpired = (expiresAt: number) =>
  dayjs.unix(expiresAt).isBefore(dayjs());

/** Statuses for which the payment link is no longer shareable (copy / resend hidden). */
const SETTLED_STATUSES = ['completed', 'refunded'];

/** Returns `true` when the payment is completed or refunded, so its link should not be shared again. */
export const isPaymentSettled = (status: string) =>
  SETTLED_STATUSES.includes(status.toLowerCase());
